import React from 'react'
import { motion } from 'framer-motion'

const EducationSection = () => {
  const education = [
    {
      id: 1,
      degree: 'Bachelor of Science in Computer Science and Engineering',
      institution: 'Shahjalal University of Science and Technology',
      period: '2010-2014',
      location: 'Sylhet, Bangladesh',
      description: 'Comprehensive study of computer science fundamentals, algorithms, data structures, software engineering, and system design.',
      achievements: [
        'Active member of Computer Science Club',
        'Participated in national programming competitions'
      ]
    },
    {
      id: 2,
      degree: 'Higher Secondary School Certificate',
      institution: 'Notre Dame College',
      period: '2009',
      location: 'Dhaka, Bangladesh',
      description: 'Science stream with focus on Mathematics, Physics, Chemistry, and Biology.',
      achievements: [
        'Top 10% in batch',
        'Science Olympiad participant'
      ]
    },
  ]

  const certifications = [
    {
      id: 1,
      title: 'Certificate on Software Testing & Quality Assurance',
      institution: 'BITM BASIS',
      period: '2019',
      location: 'Dhaka, Bangladesh',
      description: 'Comprehensive training on manual and automated testing methodologies, quality assurance processes, and best practices.',
      skills: ['Test Planning', 'Test Execution', 'Automation Testing', 'Bug Tracking']
    },
    {
      id: 2,
      title: 'International English Language Testing System (IELTS)',
      institution: 'British Council',
      period: '2024',
      location: 'Dhaka, Bangladesh',
      score: 'Band Score: 7.0',
      description: 'Demonstrated proficiency in English language communication skills across listening, reading, writing, and speaking.',
      skills: ['Professional Communication', 'Technical Documentation', 'Client Interaction']
    }
  ]

  return (
    <section id="education" className="py-20 relative">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          {/* Section header */}
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              Education & Certifications
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-400 to-purple-400 mx-auto rounded-full" />
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Education */}
            <div>
              <motion.h3
                className="text-2xl font-bold text-white mb-8 text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                Education
              </motion.h3>

              <div className="space-y-8">
                {education.map((edu, index) => (
                  <motion.div
                    key={edu.id}
                    className="glass-light rounded-2xl p-6 hover:bg-white/10 transition-all duration-300"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.2 }}
                    viewport={{ once: true }}
                    whileHover={{ y: -5 }}
                  >
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-4">
                      <div>
                        <h4 className="text-xl font-bold text-white mb-2">{edu.degree}</h4>
                        <h5 className="text-lg text-blue-400 font-medium mb-1">{edu.institution}</h5>
                        <p className="text-gray-400 text-sm">{edu.location}</p>
                      </div>
                      <span className="bg-blue-500/20 text-blue-400 px-3 py-1 rounded-full text-sm font-medium mt-2 md:mt-0">
                        {edu.period}
                      </span>
                    </div>

                    <p className="text-gray-300 mb-4 text-sm leading-relaxed">
                      {edu.description}
                    </p>

                    {edu.achievements && edu.achievements.length > 0 && (
                      <div>
                        <h6 className="text-sm font-semibold text-white mb-2">Key Achievements:</h6>
                        <ul className="text-xs text-gray-400 space-y-1">
                          {edu.achievements.map((achievement, achIndex) => (
                            <li key={achIndex} className="flex items-start gap-2">
                              <span className="text-blue-400">•</span>
                              <span>{achievement}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Certifications */}
            <div>
              <motion.h3
                className="text-2xl font-bold text-white mb-8 text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
              >
                Certifications
              </motion.h3>

              <div className="space-y-8">
                {certifications.map((cert, index) => (
                  <motion.div
                    key={cert.id}
                    className="glass-light rounded-2xl p-6 hover:bg-white/10 transition-all duration-300"
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.2 + 0.2 }}
                    viewport={{ once: true }}
                    whileHover={{ y: -5 }}
                  >
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-4">
                      <div>
                        <h4 className="text-xl font-bold text-white mb-2">{cert.title}</h4>
                        <h5 className="text-lg text-blue-400 font-medium mb-1">{cert.institution}</h5>
                        <p className="text-gray-400 text-sm">{cert.location}</p>
                      </div>
                      <div className="text-right mt-2 md:mt-0">
                        {cert.score && (
                          <span className="bg-blue-500/20 text-blue-400 px-3 py-1 rounded-full text-sm font-medium block mb-2">
                            {cert.score}
                          </span>
                        )}
                        <span className="bg-purple-500/20 text-purple-400 px-3 py-1 rounded-full text-sm font-medium">
                          {cert.period}
                        </span>
                      </div>
                    </div>

                    <p className="text-gray-300 mb-4 text-sm leading-relaxed">
                      {cert.description}
                    </p>

                    <div>
                      <h6 className="text-sm font-semibold text-white mb-2">Skills Acquired:</h6>
                      <div className="flex flex-wrap gap-2">
                        {cert.skills.map((skill, skillIndex) => (
                          <span
                            key={skillIndex}
                            className="bg-white/10 text-gray-400 px-3 py-1 rounded-full text-xs"
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default EducationSection